
import { useState } from 'react';

const ExerciseSection = () => {
  const [selectedExercise, setSelectedExercise] = useState('writing');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);

  const exercises = {
    writing: {
      title: 'Writing Practice',
      icon: 'ri-edit-line',
      color: 'blue',
      questions: [
        {
          question: 'Complete the sentence: "I _____ to the store yesterday."',
          options: ['go', 'went', 'going', 'goes'],
          correct: 'went',
          explanation: 'We use "went" (past tense) because "yesterday" indicates a completed action in the past.'
        },
        {
          question: 'Choose the correct form: "She _____ English for 3 years."',
          options: ['studies', 'studied', 'has studied', 'study'],
          correct: 'has studied',
          explanation: 'We use present perfect "has studied" for an action that started in the past and continues to the present.'
        }
      ]
    },
    vocabulary: {
      title: 'Vocabulary Builder',
      icon: 'ri-book-open-line',
      color: 'green',
      questions: [
        {
          question: 'What does "procrastinate" mean?',
          options: ['To delay or postpone', 'To work quickly', 'To organize', 'To celebrate'],
          correct: 'To delay or postpone',
          explanation: 'Procrastinate means to delay or postpone action; put off doing something.'
        },
        {
          question: 'Choose the synonym for "enormous":',
          options: ['tiny', 'huge', 'medium', 'normal'],
          correct: 'huge',
          explanation: 'Enormous means extremely large or huge in size, extent, or intensity.'
        }
      ]
    },
    pronunciation: {
      title: 'Pronunciation Practice',
      icon: 'ri-mic-line',
      color: 'purple',
      questions: [
        {
          question: 'Which word has the stress on the FIRST syllable?',
          options: ['computer', 'banana', 'elephant', 'guitar'],
          correct: 'elephant',
          explanation: 'Elephant is stressed on the first syllable: EL-e-phant. The others are stressed differently.'
        },
        {
          question: 'How many syllables does "beautiful" have?',
          options: ['2', '3', '4', '5'],
          correct: '3',
          explanation: 'Beautiful has 3 syllables: beau-ti-ful.'
        }
      ]
    },
    listening: {
      title: 'Listening Comprehension',
      icon: 'ri-headphone-line',
      color: 'orange',
      questions: [
        {
          question: 'Listen to the audio: "The weather is _____ today."',
          options: ['sunny', 'rainy', 'cloudy', 'snowy'],
          correct: 'sunny',
          explanation: 'The audio clearly states "The weather is sunny today."'
        },
        {
          question: 'What time was mentioned in the conversation?',
          options: ['3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'],
          correct: '3:30 PM',
          explanation: 'The speaker mentioned meeting at three-thirty in the afternoon.'
        }
      ]
    }
  };

  const currentExercise = exercises[selectedExercise as keyof typeof exercises];
  const currentQ = currentExercise.questions[currentQuestion];

  const handleAnswer = (answer: string) => {
    setUserAnswer(answer);
    setShowResult(true);
    if (answer === currentQ.correct) {
      setScore(score + 1);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < currentExercise.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswer('');
      setShowResult(false);
    } else {
      // Exercise completed
      alert(`Exercise completed! Your score: ${score + (userAnswer === currentQ.correct ? 1 : 0)}/${currentExercise.questions.length}`);
      setCurrentQuestion(0);
      setScore(0);
      setUserAnswer('');
      setShowResult(false);
    }
  };

  const getColorClasses = (color: string, variant: 'bg' | 'text' | 'border') => {
    const colorMap = {
      blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-500' },
      green: { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-500' },
      purple: { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-500' },
      orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-500' },
    };
    return colorMap[color as keyof typeof colorMap][variant];
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Interactive Practice Exercises
            </h2>
            <p className="text-xl text-gray-600">
              Test your knowledge and improve your skills with fun, interactive exercises
            </p>
          </div>

          {/* Exercise Type Selector */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.entries(exercises).map(([key, exercise]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedExercise(key);
                  setCurrentQuestion(0);
                  setUserAnswer('');
                  setShowResult(false);
                  setScore(0);
                }}
                className={`p-4 rounded-xl text-center transition-all cursor-pointer ${
                  selectedExercise === key
                    ? `${getColorClasses(exercise.color, 'bg')} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:shadow-md'
                }`}
              >
                <i className={`${exercise.icon} text-2xl mb-2 block`}></i>
                <span className="font-semibold text-sm">{exercise.title}</span>
              </button>
            ))}
          </div>

          {/* Exercise Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{currentExercise.title}</h3>
              <span className="text-sm text-gray-500">
                Question {currentQuestion + 1} of {currentExercise.questions.length}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getColorClasses(currentExercise.color, 'bg')}`}
                style={{ width: `${((currentQuestion + 1) / currentExercise.questions.length) * 100}%` }}
              ></div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-6">{currentQ.question}</h4>
              
              {/* Audio Player for Listening Exercises */}
              {selectedExercise === 'listening' && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <button className={`${getColorClasses(currentExercise.color, 'bg')} text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-colors whitespace-nowrap cursor-pointer`}>
                    <i className="ri-play-line mr-2"></i>
                    Play Audio
                  </button>
                </div>
              )}

              {/* Answer Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentQ.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => !showResult && handleAnswer(option)}
                    disabled={showResult}
                    className={`p-4 rounded-lg border-2 text-left transition-all cursor-pointer ${
                      showResult
                        ? option === currentQ.correct
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : option === userAnswer && option !== currentQ.correct
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 bg-gray-50 text-gray-500'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{String.fromCharCode(65 + index)}.</span> {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Result and Explanation */}
            {showResult && (
              <div className={`p-6 rounded-lg mb-6 ${
                userAnswer === currentQ.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center mb-3">
                  <i className={`${userAnswer === currentQ.correct ? 'ri-checkbox-circle-fill text-green-600' : 'ri-close-circle-fill text-red-600'} text-xl mr-2`}></i>
                  <span className={`font-semibold ${userAnswer === currentQ.correct ? 'text-green-700' : 'text-red-700'}`}>
                    {userAnswer === currentQ.correct ? 'Correct!' : 'Incorrect'}
                  </span>
                </div>
                <p className="text-gray-700">{currentQ.explanation}</p>
              </div>
            )}

            {/* Next Button */}
            {showResult && (
              <div className="text-center">
                <button
                  onClick={nextQuestion}
                  className={`${getColorClasses(currentExercise.color, 'bg')} text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors whitespace-nowrap cursor-pointer`}
                >
                  {currentQuestion < currentExercise.questions.length - 1 ? 'Next Question' : 'Complete Exercise'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExerciseSection;
